import { StatCardData } from '@/types/StatCardData';
import { formatNumber } from '@/utils/gameUtils';
import { formatCurrency } from '@/utils/gameUtils';
import { useGameStore } from '@/stores/gameStore';
import { computed } from 'vue';

function getStorageProgress(current: number | undefined, capacity: number | undefined): number {
    if (!current || !capacity || capacity === 0) return 0;
    return Math.min(100, (current / capacity) * 100);
}

export function getStatCards(): StatCardData[] {
  const store = useGameStore();
  const stats = computed(() => store.gameStats);

  return [
    new StatCardData({
      label: 'Raw Data',
      isStatVisible: () => true,
      props: {
        label: 'Raw Data',
        value: formatNumber(stats.value.resources.rawData || 0),
        storageInfo: formatNumber(stats.value.storageCapacity.rawData || 0),
        progress: getStorageProgress(stats.value.resources.rawData, stats.value.storageCapacity.rawData),
      },
    }),
    new StatCardData({
      label: 'Clean Data',
      isStatVisible: (gameState) => {
        return gameState?.upgrades?.dataCleaning || false;
      },
      props: {
        label: 'Clean Data',
        value: formatNumber(stats.value.resources.cleanData || 0),
        storageInfo: formatNumber(stats.value.storageCapacity.cleanData || 0),
        progress: getStorageProgress(stats.value.resources.cleanData, stats.value.storageCapacity.cleanData),
      },
    }),
    new StatCardData({
      label: 'Features',
      isStatVisible: (gameState) => gameState?.upgrades?.ml && (gameState?.personnel?.mlEngineer || 0) > 0,
      props: {
        label: 'Features',
        value: formatNumber(stats.value.resources.feature || 0),
        storageInfo: formatNumber(stats.value.storageCapacity.feature || 0),
        progress: getStorageProgress(stats.value.resources.feature, stats.value.storageCapacity.feature),
      },
    }),
    new StatCardData({
      label: 'Models',
      isStatVisible: (gameState) => {
        return gameState?.upgrades?.ml && (gameState?.personnel?.mlEngineer || 0) > 0;
      },
      props: {
        label: 'Models',
        value: formatNumber(stats.value.resources.model || 0),
        storageInfo: formatNumber(stats.value.storageCapacity.model || 0),
        progress: getStorageProgress(stats.value.resources.model, stats.value.storageCapacity.model),
      },
    }),
    new StatCardData({
      label: 'Research Points',
      isStatVisible: (gameState) => gameState?.upgrades?.advancedAI && (gameState?.personnel?.aiResearcher || 0) > 0,
      props: {
        label: 'Research Points',
        value: formatNumber(stats.value.resources.researchProgress || 0),
        storageInfo: formatNumber(stats.value.storageCapacity.researchProgress || 0),
        progress: getStorageProgress(stats.value.resources.researchProgress, stats.value.storageCapacity.researchProgress),
      },
    }),
    new StatCardData({
      label: 'Reasoning Engines',
      isStatVisible: (gameState) => {
        return (gameState?.personnel?.reasoningSpecialist || 0) > 0;
      },
      props: {
        label: 'Reasoning Engines',
        value: formatNumber(stats.value.resources.reasoningEngine || 0),
        storageInfo: formatNumber(stats.value.storageCapacity.reasoningEngine || 0),
        progress: getStorageProgress(stats.value.resources.reasoningEngine, stats.value.storageCapacity.reasoningEngine),
      },
    }),
    new StatCardData({
      label: 'Money',
      isStatVisible: () => true,
      props: {
        label: 'Money',
        value: formatCurrency((stats.value.resources.money || 0)),
      },
    }),
    new StatCardData({
      label: 'Raw Data/sec',
      isStatVisible: () => true,
      props: {
        label: 'Raw Data/sec',
        value: `${formatNumber(stats.value.productionRates.rawData || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Clean Data/sec',
      isStatVisible: (gameState) => {
        return gameState?.upgrades?.dataCleaning || false;
      },
      props: {
        label: 'Clean Data/sec',
        value: `${formatNumber(stats.value.productionRates.cleanData || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Features/sec',
      isStatVisible: (gameState) => gameState?.upgrades?.ml && (gameState?.personnel?.mlEngineer || 0) > 0,
      props: {
        label: 'Features/sec',
        value: `${formatNumber(stats.value.productionRates.feature || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Models/sec',
      isStatVisible: (gameState) => {
        return gameState?.upgrades?.ml || false;
      },
      props: {
        label: 'Models/sec',
        value: `${formatNumber(stats.value.productionRates.model || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Research Points/sec',
      isStatVisible: (gameState) => gameState?.upgrades?.advancedAI && (gameState?.personnel?.aiResearcher || 0) > 0,
      props: {
        label: 'Research Points/sec',
        value: `${formatNumber(stats.value.productionRates.researchProgress || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Reasoning Engines/sec',
      isStatVisible: (gameState) => {
        return (gameState?.personnel?.reasoningSpecialist || 0) > 0
      },
      props: {
        label: 'Reasoning Engines/sec',
        value: `${formatNumber(stats.value.productionRates.reasoningEngine || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Income/sec',
      isStatVisible: () => true,
      props: {
        label: 'Income/sec',
        value: `${formatCurrency(stats.value.productionRates.money || 0)}/s`,
      },
    }),
    new StatCardData({
      label: 'Infrastructure/sec',
      isStatVisible: (gameState) => {
        return (gameState?.expenses?.infrastructureExpensesPerSec || 0) > 0;
      },
      props: {
        label: 'Infrastructure/sec',
        value: `${formatCurrency(stats.value.expenses.infrastructureExpensesPerSec || 0)}/s`,
        valueColor: '#ffaa00',
      },
    }),
    new StatCardData({
      label: 'Talent/sec',
      isStatVisible: (gameState) => {
        return (gameState?.expenses?.talentExpensesPerSec || 0) > 0;
      },
      props: {
        label: 'Talent/sec',
        value: `${formatCurrency(stats.value.expenses.talentExpensesPerSec || 0)}/s`,
        valueColor: '#ffaa00',
      },
    }),
    new StatCardData({
      label: 'Total Expenses/sec',
      isStatVisible: (gameState) => {
          return (gameState?.expenses?.totalExpensesPerSec || 0) > 0;
        },
      props: {
        label: 'Total Expenses/sec',
        value: `${formatCurrency(stats.value.expenses.totalExpensesPerSec || 0)}/s`,
        valueColor: '#ff4444',
      },
    }),
    new StatCardData({
      label: 'Net Profit/sec',
      isStatVisible: () => true,
      props: {
        label: 'Net Profit/sec',
        value: `${formatCurrency(stats.value.netProfitPerSec)}/s`,
        valueColor: stats.value.netProfitPerSec >= 0 ? '#00ff88' : '#ff4444',
      },
    }),
    new StatCardData({
      label: 'Infrastructure Reliability',
      isStatVisible: (gameState) => {
        return (gameState?.infrastructureFailureRate || 0) > 0;
      },
      props: {
        label: 'Infrastructure Reliability',
        value: `${Math.floor(stats.value.infrastructureReliability || 0)}%`,
        progress: Math.floor(stats.value.infrastructureReliability || 0),
        progressColor: stats.value.infrastructureReliability >= 90 ? 'linear-gradient(90deg, #00ff88, #00cc6a)' : stats.value.infrastructureReliability >= 70 ? 'linear-gradient(90deg, #ffaa00, #ff8800)' : 'linear-gradient(90deg, #ff4444, #cc0000)',
        extraInfo: `Failure Rate: ${((1 - (stats.value.infrastructureReliability || 0) / 100) * 100).toFixed(1)}%`,
      },
    }),
    new StatCardData({
      label: 'AGI Progress',
      isStatVisible: (gameState) => (gameState?.agiProgress || 0) > 0,
      props: {
        label: 'AGI Progress',
        value: `${Math.floor(stats.value.agiProgress || 0)}%`,
        progress: Math.floor(stats.value.agiProgress || 0),
      },
    }),
  ];
}